import { useEffect, useMemo, useRef, useState } from "react";

import {
  FEED_EVENTS,
  RECONNECT_BACKOFF_FACTOR,
  RECONNECT_BASE_DELAY_MS,
  RECONNECT_MAX_DELAY_MS,
  SNAPSHOT_EVENT,
  STATUS_FEED_URL,
} from "./status-feed.constants";
import {
  deriveStatusView,
  type ServiceStatusView,
  type StatusFeedIssue,
  type StatusFeedIssueUpdate,
} from "./service-status.model";

/**
 * The feed's two independent pieces of state, exactly as Hund's own client
 * keeps them (`hundio/status.js`, `src/status.coffee`): an overall state
 * string, and the set of active issues. Basic events move only the first,
 * issue events only the second.
 */
export interface FeedState {
  state: string | null;
  issues: StatusFeedIssue[];
}

export const INITIAL_FEED_STATE: FeedState = { state: null, issues: [] };

interface FeedEventData {
  state?: string;
  issues?: StatusFeedIssue[];
  issue?: StatusFeedIssue;
  update?: StatusFeedIssueUpdate;
}

/** Hund's own contract: parse failures degrade to an empty payload. */
function parseEventData(raw: string): FeedEventData {
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object"
      ? (parsed as FeedEventData)
      : {};
  } catch {
    return {};
  }
}

/**
 * Replace an issue in place if we already know its id, otherwise prepend it —
 * newest incident first, so it wins the banner headline.
 */
function upsertIssue(
  issues: StatusFeedIssue[],
  issue: StatusFeedIssue
): StatusFeedIssue[] {
  const index = issues.findIndex((existing) => existing.id === issue.id);
  if (index === -1) return [issue, ...issues];
  const next = [...issues];
  next[index] = issue;
  return next;
}

function removeIssue(
  issues: StatusFeedIssue[],
  id: string | undefined
): StatusFeedIssue[] {
  if (!id) return issues;
  return issues.filter((issue) => issue.id !== id);
}

/**
 * Pure reducer for one named SSE event. Mirrors Hund's handler table:
 *
 * - `init_event`   — full snapshot: overall state **and** a fresh issue list.
 * - `status_created` / `degraded` / `restored` — overall state only.
 * - `issue_created` / `issue_started` — add `data.issue` with empty `updates`.
 * - `issue_updated` — unshift `data.update` onto that issue (newest-first).
 * - `issue_resolved` / `issue_cancelled` / `issue_ended` — drop the issue.
 * - `issue_reopened` — re-insert `data.update.issue`.
 *
 * Unknown events and unusable payloads leave state untouched.
 */
export function reduceFeedEvent(
  current: FeedState,
  eventName: string,
  rawData: string
): FeedState {
  const data = parseEventData(rawData);

  switch (eventName) {
    case SNAPSHOT_EVENT: {
      return {
        state: typeof data.state === "string" ? data.state : null,
        issues: Array.isArray(data.issues) ? data.issues : [],
      };
    }

    case "status_created":
    case "degraded":
    case "restored": {
      return typeof data.state === "string"
        ? { ...current, state: data.state }
        : current;
    }

    case "issue_created":
    case "issue_started": {
      // Hund zeroes `updates` on creation — the first real update arrives as
      // its own `issue_updated` event.
      return data.issue
        ? {
            ...current,
            issues: upsertIssue(current.issues, { ...data.issue, updates: [] }),
          }
        : current;
    }

    case "issue_updated": {
      const update = data.update;
      const index = current.issues.findIndex(
        (issue) => issue.id === update?.issue_id
      );
      if (!update || index === -1) return current;

      const issue = current.issues[index];
      const issues = [...current.issues];
      issues[index] = { ...issue, updates: [update, ...(issue.updates ?? [])] };
      return { ...current, issues };
    }

    case "issue_resolved":
    case "issue_cancelled":
    case "issue_ended": {
      return {
        ...current,
        issues: removeIssue(
          current.issues,
          data.update?.issue_id ?? data.issue?.id
        ),
      };
    }

    case "issue_reopened": {
      return data.update?.issue
        ? { ...current, issues: upsertIssue(current.issues, data.update.issue) }
        : current;
    }

    default: {
      return current;
    }
  }
}

function backoffDelay(attempt: number): number {
  const delay =
    RECONNECT_BASE_DELAY_MS * RECONNECT_BACKOFF_FACTOR ** Math.max(attempt, 0);
  return Math.min(delay, RECONNECT_MAX_DELAY_MS);
}

/**
 * One always-on `EventSource` against Hund's public status feed, for the life
 * of the session. Mounted exactly once (by `ServiceStatusProvider`) so the
 * header dot and the banner share a single connection.
 *
 * Failure is silent by design: on any error we drop the state so the UI shows
 * *nothing* rather than a stale green "all good" — the one outcome that would
 * be worse than no indicator at all — then reconnect with exponential backoff.
 * The reconnect's `init_event` re-syncs both the state and the issue list.
 */
export function useServiceStatusFeed(): ServiceStatusView {
  const [feed, setFeed] = useState<FeedState>(INITIAL_FEED_STATE);
  const attemptRef = useRef(0);

  useEffect(() => {
    // SSR and very old browsers have no EventSource. Stay in the silent
    // error state rather than throwing on mount.
    if (typeof window === "undefined" || typeof EventSource === "undefined") {
      return;
    }

    let disposed = false;
    let source: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

    const closeSource = () => {
      if (!source) return;
      try {
        source.close();
      } catch {
        // A half-open stream can throw on close; nothing useful to do.
      }
      source = null;
    };

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer !== undefined) return;
      const delay = backoffDelay(attemptRef.current);
      attemptRef.current += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = undefined;
        connect();
      }, delay);
    };

    function connect() {
      if (disposed) return;
      closeSource();

      let opened: EventSource;
      try {
        opened = new EventSource(STATUS_FEED_URL);
      } catch {
        scheduleReconnect();
        return;
      }
      source = opened;

      FEED_EVENTS.forEach((eventName) => {
        opened.addEventListener(eventName, (event) => {
          // A snapshot proves the stream is healthy again.
          if (eventName === SNAPSHOT_EVENT) attemptRef.current = 0;
          const { data } = event as MessageEvent<string>;
          setFeed((current) => reduceFeedEvent(current, eventName, data));
        });
      });

      opened.addEventListener("error", () => {
        // EventSource retries on its own; we take over so the cadence is ours
        // and the UI stops claiming a status it can no longer verify.
        setFeed(INITIAL_FEED_STATE);
        closeSource();
        scheduleReconnect();
      });
    }

    connect();

    return () => {
      disposed = true;
      if (reconnectTimer !== undefined) clearTimeout(reconnectTimer);
      closeSource();
    };
  }, []);

  return useMemo(() => deriveStatusView(feed.state, feed.issues), [feed]);
}
